import { arePairHalves, isPairSeatType, pairKindOf, pairSideOf } from '@cineamo/api/models/seat/seat-pairs';
import { SeatType } from '@cineamo/api/models/seat/SeatDto.types';

import { getRelativeLuminance, hexToRgb } from '@cineamo/ui/src/helpers/color-helper';

import { Point2D, deg2Rad, rectContainsPoint, rotatePoint } from '../helpers/math';
import { outlinePathD } from '../room-3d/outline/outline';
import { roomOutline } from '../room-3d/outline/room-geometry';
import { DBOX_COLOR, LOCKED_SEAT_COLOR } from '../seat-map-3d/seat-colors';

import { CameraState } from './Camera';
import { seatLabel } from './seat-label';
import { SeatMapData } from './SeatMap';
import { Position, RoomObject, Seat, SeatCategory } from './typedefs';

// The screen as drawn in the Cineamo 4.0 booking design: label and line colors, and the curve's depth and line
// thickness as fractions of the screen's width.
// Darker than the line: at the line's own grey the label vanished on the light canvas.
const SCREEN_LABEL_COLOR = '#6E6E80';
const SCREEN_LINE_CENTER_COLOR = '#B3B3BD';
const SCREEN_LINE_EDGE_COLOR = '#DBDBE2';
const SCREEN_CURVE_SAG = 0.0276;
const SCREEN_LINE_WIDTH = 0.011;
/** How far in front of the front wall (plan y = 0, mm) a screen may stand and still count as the room's. */
const SCREEN_MAX_FRONT_MM = 2000;

/**
 * The room's screen, when it stands where a screen can: near the front wall and within the room's width. One
 * placed far outside (bad data) would draw the screen off the map, so the map falls back to its top line.
 */
export function usableScreenObject(
    roomObjects: RoomObject[],
    room: { width: number; depth: number }
): RoomObject | undefined {
    const screen = roomObjects.find((it) => it.type === 'screen');
    const transform = screen?.dto._embedded?.transform;
    if (!screen || !transform) return screen;
    const x = transform.positionX ?? 0;
    const y = transform.positionY ?? 0;
    const isInside = Math.abs(x) <= room.width / 2 && y <= SCREEN_MAX_FRONT_MM && y >= -room.depth;
    return isInside ? screen : undefined;
}

export type HEX_COLOR = `#${string}`;

export interface Transform {
    scale: number;
    offset: Point2D;
}

export interface DrawerOptions {
    seatCornerRadius: number;
    tableCornerRadius: number;
    seatColorsByCategory: { [key: SeatCategory['name']]: HEX_COLOR };
    selectedSeatColor?: HEX_COLOR;
    selectedSeatCenterColor: HEX_COLOR;
    screenLabel: string;
    icons?: Record<string, HTMLImageElement>;
}

export const defaultOptions: DrawerOptions = {
    seatCornerRadius: 4,
    tableCornerRadius: 4,
    seatColorsByCategory: {
        standard: '#00FFDC',
        premium: '#FFA620',
        love: '#FFDE2E',
        VIP: '#6BAFFF'
    },
    selectedSeatCenterColor: '#000023',
    screenLabel: 'Canvas'
};

const OVERHEAD_OBJECT_TYPES: ReadonlySet<string> = new Set(['speaker', 'projector']);
const FIXTURE_FILL = 'rgba(128, 128, 145, 0.28)';
/** Emergency exits in the safety-sign green, so guests find them on the map. */
const EXIT_FILL = '#1E9B4E';
const EXIT_STROKE = '#15743A';
const FIXTURE_STROKE = 'rgba(128, 128, 145, 0.6)';

// The room as the studio plan draws it (CIN-15190): a faint floor inside its walls, seats with rounded corners,
// a darker backrest on the back edge and, once there is room to read it, the seat's name.
const FLOOR_FILL = 'rgba(128, 128, 145, 0.08)';
const WALL_STROKE = 'rgba(128, 128, 145, 0.45)';
const SEAT_OUTLINE = 'rgba(0, 0, 35, 0.55)';
const BACKREST_FILL = 'rgba(0, 0, 35, 0.3)';
const INK_ON_LIGHT = '#000023';
const INK_ON_DARK = '#F2F2F2';
/** Seat labels appear once a seat is this many CSS pixels across. */
const SEAT_LABEL_MIN_PX = 26;
/** A bed's hood is a narrow band at the head end, at most this deep (mm). */
const BED_BACKREST_MAX_MM = 250;

const floorPaths = new WeakMap<object, Path2D>();

function floorPath(room: SeatMapData['room']): Path2D {
    const outline = roomOutline(room);
    let path = floorPaths.get(outline);
    if (!path) {
        path = new Path2D(outlinePathD(outline, true));
        floorPaths.set(outline, path);
    }
    return path;
}

export class Drawer {
    private data?: SeatMapData;
    private options: DrawerOptions;
    /** One device pixel in world units, and one CSS pixel in device pixels, for the frame being drawn. */
    private hairline = 1;
    private cssPixel = 1;

    get seatCornerRadius() {
        return this.options.seatCornerRadius;
    }

    constructor(
        private ctx: CanvasRenderingContext2D,
        options: Partial<DrawerOptions> = {}
    ) {
        this.options = {
            ...defaultOptions,
            ...options
        };
    }

    /**
     * Draw a complete frame with camera transform applied.
     * This is the primary render method used by the spring-animated SeatMap.
     */
    drawFrame(data: SeatMapData, camera: CameraState) {
        this.data = data;

        const canvasWidth = this.ctx.canvas.width;
        const canvasHeight = this.ctx.canvas.height;
        if (!canvasWidth || !canvasHeight) return;

        // Reset transform and clear
        this.ctx.setTransform(1, 0, 0, 1, 0, 0);
        this.ctx.clearRect(0, 0, canvasWidth, canvasHeight);

        // Apply camera transform
        this.ctx.setTransform(camera.scale, 0, 0, camera.scale, camera.x, camera.y);
        this.hairline = 1 / camera.scale;
        this.cssPixel = canvasWidth / (this.ctx.canvas.clientWidth || canvasWidth);

        // Draw scene in world space
        const canvasHalfWidth = canvasWidth / 2;
        const roomScale = canvasWidth / data.room.width;
        const screenDepth = this.calculateScreenDepth(data.roomObjects ?? [], canvasHeight, roomScale);

        this.drawFloor(data.room, roomScale, canvasHalfWidth, screenDepth);
        this.drawScreen(canvasWidth);

        // Room objects (tables) behind seats
        if (data.roomObjects) {
            for (const roomObject of data.roomObjects.filter(this.NotScreen)) {
                this.drawRoomObject(roomObject, roomScale, canvasHalfWidth, screenDepth);
            }
        }

        // Loveseat bridges behind individual seats
        this.drawPairBridges(data.seats, roomScale, canvasHalfWidth, screenDepth);

        // Individual seats
        for (const seat of data.seats) {
            this.drawSeat(seat, roomScale, canvasHalfWidth, screenDepth);
        }
    }

    /**
     * Backward-compatible draw without camera (uses identity transform).
     */
    draw(data: SeatMapData, options: Partial<DrawerOptions> = {}) {
        this.data = data;
        this.options = { ...this.options, ...options };
        this.drawFrame(data, { x: 0, y: 0, scale: 1 });
    }

    // Filter function to exclude screens from other room objects
    private NotScreen = (roomObjectA: RoomObject) => roomObjectA.type !== 'screen';

    /**
     * Backward-compatible redraw.
     */
    redraw(options: Partial<DrawerOptions> = {}) {
        this.options = { ...this.options, ...options };
        if (this.data) this.draw(this.data);
    }

    findSeatByPoint(point: Point2D): Seat | null {
        if (!this.data) return null;

        const canvasWidth = this.ctx.canvas.width;
        const canvasHeight = this.ctx.canvas.height;
        const canvasHalfWidth = this.ctx.canvas.width / 2;
        const roomScale = canvasWidth / this.data.room.width;
        const screenDepth = this.calculateScreenDepth(this.data.roomObjects ?? [], canvasHeight, roomScale);
        for (const seat of this.data.seats) {
            const seatPosition = this.translateSeatPosition(seat, roomScale, canvasHalfWidth, screenDepth);
            const width = seat.width * roomScale,
                depth = seat.depth * roomScale,
                halfWidth = width / 2,
                halfDepth = depth / 2;

            const rotatedPoint = rotatePoint(point, seatPosition, seat.rotation.z);
            if (
                rectContainsPoint(
                    {
                        top: seatPosition.y - halfDepth,
                        right: seatPosition.x + halfWidth,
                        bottom: seatPosition.y + halfDepth,
                        left: seatPosition.x - halfWidth
                    },
                    rotatedPoint
                )
            ) {
                return seat;
            }
        }

        return null;
    }

    /**
     * Draws the screen at the top of the room: a line curved like a cinema screen, highest in the middle and fading
     * to a lighter grey at both ends, with its label just above.
     */
    private drawScreen(canvasWidth: number) {
        const screenObject = this.getScreenObject(this.data?.roomObjects || []);
        const roomScale = canvasWidth / (this.data?.room.width || 1);
        const canvasHalfWidth = canvasWidth / 2;

        const fontSize = 28;

        let startX: number;
        let endX: number;
        let lineY: number;

        if (screenObject) {
            const transform = screenObject.dto._embedded?.transform;
            const screenWidth = screenObject.width * roomScale;
            const screenCenterX = (transform?.positionX ?? 0) * roomScale + canvasHalfWidth;
            const screenCenterY = -(transform?.positionY ?? -screenObject.height) * roomScale + (fontSize + 2);
            startX = screenCenterX - screenWidth / 2;
            endX = screenCenterX + screenWidth / 2;
            lineY = screenCenterY + (screenObject.depth * roomScale) / 2;
        } else {
            const horizontalPadding = canvasWidth * 0.02;
            startX = horizontalPadding;
            endX = canvasWidth - horizontalPadding;
            lineY = fontSize + 12;
        }

        const width = endX - startX;
        const lineWidth = Math.max(4, width * SCREEN_LINE_WIDTH);
        const labelGap = fontSize * 0.35;
        // The label sits just above the curve's peak; where the canvas top leaves no room for it, the curve is
        // flattened and, if that is not enough, moved down, so the two never touch.
        const lowestPeak = fontSize + labelGap + lineWidth / 2;
        lineY = Math.max(lineY, lowestPeak);
        const sag = Math.max(0, Math.min(width * SCREEN_CURVE_SAG, lineY - lowestPeak));
        const peakY = lineY - sag;

        this.ctx.font = `700 ${fontSize}px Montserrat`;
        this.ctx.fillStyle = SCREEN_LABEL_COLOR;
        this.ctx.textAlign = 'center';
        this.ctx.fillText(
            this.options.screenLabel,
            startX + width / 2,
            Math.max(fontSize, peakY - lineWidth / 2 - labelGap)
        );

        const gradient = this.ctx.createLinearGradient(startX, 0, endX, 0);
        gradient.addColorStop(0, SCREEN_LINE_EDGE_COLOR);
        gradient.addColorStop(0.37, SCREEN_LINE_CENTER_COLOR);
        gradient.addColorStop(0.63, SCREEN_LINE_CENTER_COLOR);
        gradient.addColorStop(1, SCREEN_LINE_EDGE_COLOR);

        this.ctx.beginPath();
        this.ctx.moveTo(startX, lineY);
        // A quadratic curve peaks halfway to its control point, so the control point sits at twice the sag.
        this.ctx.quadraticCurveTo(startX + width / 2, lineY - sag * 2, endX, lineY);
        this.ctx.lineWidth = lineWidth;
        this.ctx.lineCap = 'square';
        this.ctx.strokeStyle = gradient;
        this.ctx.stroke();
        this.ctx.lineCap = 'butt';
    }

    private drawFloor(room: SeatMapData['room'], roomScale: number, canvasHalfWidth: number, screenDepth: number) {
        this.ctx.save();
        this.ctx.translate(canvasHalfWidth, screenDepth);
        this.ctx.scale(roomScale, roomScale);
        const path = floorPath(room);
        this.ctx.fillStyle = FLOOR_FILL;
        this.ctx.fill(path);
        this.ctx.strokeStyle = WALL_STROKE;
        this.ctx.lineWidth = (this.hairline * 1.5) / roomScale;
        this.ctx.lineJoin = 'round';
        this.ctx.stroke(path);
        this.ctx.restore();
    }

    private getScreenObject(roomObjects: RoomObject[]): RoomObject | undefined {
        return this.data ? usableScreenObject(roomObjects, this.data.room) : undefined;
    }

    private calculateScreenDepth(roomObjects: RoomObject[], canvasHeight: number, roomScale: number): number {
        const screenObject = this.getScreenObject(roomObjects);
        if (screenObject) {
            return screenObject.depth * roomScale;
        }

        const fontSize = 28;
        const verticalOffset = fontSize + 2;
        const verticalPadding = canvasHeight * 0.06 + verticalOffset;
        return verticalPadding;
    }

    /**
     * Draw connecting bridges between the two halves of a pair (loveseat, bed)
     */
    private drawPairBridges(seats: Seat[], roomScale: number, canvasHalfWidth: number, screenDepth: number) {
        // Group seats by row
        const rowGroups = new Map<string, Seat[]>();
        for (const seat of seats) {
            if (!isPairSeatType(seat.type)) continue;
            const row = seat.rowName || 'unknown';
            if (!rowGroups.has(row)) rowGroups.set(row, []);
            rowGroups.get(row)!.push(seat);
        }

        for (const rowSeats of rowGroups.values()) {
            const sorted = [...rowSeats].sort((a, b) => a.position.x - b.position.x);
            const usedIds = new Set<number>();

            for (const left of sorted) {
                if (pairSideOf(left.type) !== 'left' || usedIds.has(left.id)) continue;

                const leftPos = this.translateSeatPosition(left, roomScale, canvasHalfWidth, screenDepth);
                const leftHalfW = (left.width * roomScale) / 2;
                const leftRightEdge = leftPos.x + leftHalfW;

                // Find the nearest right half of the same kind to the right
                for (const right of sorted) {
                    if (!arePairHalves(left.type, right.type) || usedIds.has(right.id)) continue;
                    const rightPos = this.translateSeatPosition(right, roomScale, canvasHalfWidth, screenDepth);
                    const rightHalfW = (right.width * roomScale) / 2;
                    const rightLeftEdge = rightPos.x - rightHalfW;

                    if (rightLeftEdge < leftRightEdge) continue;

                    const bridgeWidth = rightLeftEdge - leftRightEdge;
                    if (bridgeWidth <= 0 || bridgeWidth > left.width * roomScale * 2) continue;

                    usedIds.add(left.id);
                    usedIds.add(right.id);

                    // Use same color as the pair
                    const color = this.getSeatColor(left);
                    const bridgeDepth = Math.max(left.depth, right.depth) * roomScale;
                    const bridgeY = Math.min(leftPos.y, rightPos.y) - bridgeDepth / 2;

                    this.ctx.beginPath();
                    this.ctx.rect(leftRightEdge, bridgeY, bridgeWidth, bridgeDepth);
                    this.ctx.fillStyle = color;
                    this.ctx.fill();
                    break;
                }
            }
        }
    }

    private drawSeat(seat: Seat, roomScale: number, canvasHalfWidth: number, screenDepth?: number) {
        const position = this.translateSeatPosition(seat, roomScale, canvasHalfWidth, screenDepth);
        const rotation = deg2Rad(seat.rotation.z);
        const width = seat.width * roomScale,
            depth = seat.depth * roomScale,
            halfWidth = width / 2,
            halfDepth = depth / 2;

        this.ctx.translate(position.x, position.y);
        this.ctx.rotate(rotation);

        // As in the studio plan: a beanbag is a round cushion, everything else a rounded rectangle, and the two
        // halves of a pair (loveseat, bed) have square inner corners so they read as one piece.
        const isBeanbag = seat.type === SeatType.BEANBAG;
        const side = pairSideOf(seat.type);
        const radius = isBeanbag ? Math.min(halfWidth, halfDepth) : Math.min(width, depth) * 0.18;
        const corners = side === 'left' ? [radius, 0, 0, radius] : side === 'right' ? [0, radius, radius, 0] : [radius];
        this.ctx.beginPath();
        this.ctx.roundRect(-halfWidth, -halfDepth, width, depth, corners);
        const seatColor = this.getSeatColor(seat);
        this.ctx.fillStyle = seatColor;
        this.ctx.fill();
        this.ctx.strokeStyle = SEAT_OUTLINE;
        this.ctx.lineWidth = this.hairline;
        this.ctx.stroke();
        const isSeatColorLight = getRelativeLuminance(hexToRgb(seatColor)) > 0.5;

        // The backrest sits on the back edge: the seat faces the screen.
        if (!isBeanbag) {
            const backrest =
                pairKindOf(seat.type) === 'bed' ? Math.min(depth * 0.1, BED_BACKREST_MAX_MM * roomScale) : depth * 0.2;
            this.ctx.beginPath();
            this.ctx.roundRect(
                -halfWidth,
                halfDepth - backrest,
                width,
                backrest,
                side === 'left' ? [0, 0, 0, radius] : side === 'right' ? [0, 0, radius, 0] : [0, 0, radius, radius]
            );
            this.ctx.fillStyle = BACKREST_FILL;
            this.ctx.fill();
        }

        // A D-BOX seat: the strip the 3D map draws on its front foot (DboxMarkers), so the legend's key holds in 2D too.
        if (seat.isDbox && ![SeatType.WHEELCHAIR, SeatType.DISABLED].includes(seat.type)) {
            const strip = depth * 0.12;
            this.ctx.beginPath();
            this.ctx.roundRect(-halfWidth * 0.7, -halfDepth + strip * 0.5, width * 0.7, strip, strip / 3);
            this.ctx.fillStyle = DBOX_COLOR;
            this.ctx.fill();
        }

        const innerIconHalfSize = (Math.min(halfWidth, halfDepth) / 3) * 2;

        let seatIcon = null;
        if (seat.isSelected) {
            seatIcon = this.options.icons?.['dot'];
        } else if (seat.isLocked) {
            seatIcon = this.options.icons?.['close'];
        } else if ([SeatType.WHEELCHAIR, SeatType.DISABLED].includes(seat.type)) {
            seatIcon = isSeatColorLight
                ? this.options.icons?.['wheelchair_dark']
                : this.options.icons?.['wheelchair_light'];
        } else if (pairKindOf(seat.type) === 'loveseat') {
            seatIcon = isSeatColorLight ? this.options.icons?.['heart_dark'] : this.options.icons?.['heart_light'];
        } else if (pairKindOf(seat.type) === 'bed') {
            seatIcon = isSeatColorLight ? this.options.icons?.['bed_dark'] : this.options.icons?.['bed_light'];
        } else if (seat.type === SeatType.BEANBAG) {
            seatIcon = isSeatColorLight ? this.options.icons?.['beanbag_dark'] : this.options.icons?.['beanbag_light'];
        }

        if (seatIcon) {
            this.ctx.drawImage(
                seatIcon,
                -innerIconHalfSize,
                -innerIconHalfSize,
                innerIconHalfSize * 2,
                innerIconHalfSize * 2
            );
        } else {
            this.drawSeatLabel(seat, width, depth, rotation, isSeatColorLight);
        }

        this.ctx.rotate(-rotation);
        this.ctx.translate(-position.x, -position.y);
    }

    /** Row and seat number, upright whatever the seat's rotation, once the seat is big enough to read it. */
    private drawSeatLabel(seat: Seat, width: number, depth: number, rotation: number, isLight: boolean) {
        const label = seatLabel(seat.rowName, seat.positionName);
        const size = Math.min(width, depth);
        if (!label || (size / this.hairline) * (1 / this.cssPixel) < SEAT_LABEL_MIN_PX) return;

        this.ctx.save();
        this.ctx.translate(0, -depth * 0.06);
        this.ctx.rotate(-rotation);
        this.ctx.font = `600 ${size * (label.length > 3 ? 0.27 : 0.33)}px Montserrat`;
        this.ctx.fillStyle = isLight ? INK_ON_LIGHT : INK_ON_DARK;
        this.ctx.textAlign = 'center';
        this.ctx.textBaseline = 'middle';
        this.ctx.fillText(label, 0, 0);
        this.ctx.restore();
    }

    private translateSeatPosition(
        seat: Seat,
        roomScale: number,
        canvasHalfWidth: number,
        screenDepth?: number
    ): Position {
        return {
            x: seat.position.x * roomScale + canvasHalfWidth,
            y: -seat.position.y * roomScale + (screenDepth ?? 0),
            z: seat.position.z
        };
    }

    private drawRoomObject(roomObject: RoomObject, roomScale: number, canvasHalfWidth: number, screenDepth?: number) {
        // Ceiling-mounted equipment says nothing about where guests can sit.
        if (roomObject.type && OVERHEAD_OBJECT_TYPES.has(roomObject.type)) return;

        const position = this.translateRoomObjectPosition(roomObject, roomScale, canvasHalfWidth, screenDepth);
        const width = roomObject.width * roomScale;
        const depth = roomObject.depth * roomScale;
        const halfWidth = width / 2;
        const halfDepth = depth / 2;
        // Tables have always been drawn as plain white blocks; the room's fixtures (stage, pillars,
        // walls, stairs, doors) are muted so they read as the building rather than as something bookable.
        const isTable = roomObject.type === 'table';
        const isExit = roomObject.type === 'emergency_exit';
        const rotation = isTable ? 0 : deg2Rad(roomObject.dto._embedded?.transform?.rotationZ ?? 0);

        this.ctx.translate(position.x, position.y);
        this.ctx.rotate(rotation);

        this.ctx.beginPath();
        if (roomObject.type === 'pillar' && Math.abs(width - depth) < Math.max(width, depth) * 0.15) {
            this.ctx.ellipse(0, 0, halfWidth, halfDepth, 0, 0, Math.PI * 2);
        } else {
            this.ctx.roundRect(-halfWidth, -halfDepth, width, depth, this.seatCornerRadius);
        }
        this.ctx.fillStyle = isTable ? '#FFFFFF' : isExit ? EXIT_FILL : FIXTURE_FILL;
        this.ctx.fill();

        this.ctx.strokeStyle = isTable ? '#E5E5E5' : isExit ? EXIT_STROKE : FIXTURE_STROKE;
        this.ctx.lineWidth = 1;
        this.ctx.stroke();

        const exitIcon = isExit ? this.options.icons?.['exit'] : undefined;
        if (exitIcon) {
            // An exit is often a thin strip along the wall; the sign may stand out of it to stay legible.
            const size = Math.min(width, Math.max(depth, width * 0.5));
            this.ctx.fillStyle = EXIT_FILL;
            this.ctx.beginPath();
            this.ctx.roundRect(-size / 2, -size / 2, size, size, this.seatCornerRadius);
            this.ctx.fill();
            this.ctx.drawImage(exitIcon, -size * 0.35, -size * 0.35, size * 0.7, size * 0.7);
        }

        this.ctx.rotate(-rotation);
        this.ctx.translate(-position.x, -position.y);
    }

    private translateRoomObjectPosition(
        roomObject: RoomObject,
        roomScale: number,
        canvasHalfWidth: number,
        screenDepth?: number
    ): Position {
        const transform = roomObject.dto._embedded?.transform;
        const fakePosition = {
            x: transform?.positionX ?? 0,
            y: transform?.positionY ?? -roomObject.height,
            z: transform?.positionZ ?? 0
        };

        return {
            x: fakePosition.x * roomScale + canvasHalfWidth,
            y: -fakePosition.y * roomScale + (screenDepth ?? 0) + 8,
            z: fakePosition.z
        };
    }

    private getSeatColor(seat: Seat): HEX_COLOR {
        let color =
            seat.isSelected && this.options.selectedSeatColor
                ? this.options.selectedSeatColor
                : (this.options.seatColorsByCategory?.[seat.category?.name ?? ''] ?? '#00FFDC');

        // Taken seats in the 3D map's grey with a solid cross: a faded category color read as just another seat.
        if (seat.isLocked) {
            color = LOCKED_SEAT_COLOR;
        }

        if (seat.isError) {
            color = '#FF3933';
        }
        return color;
    }
}
